import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Image, Filter, List, Grid, Download, BarChart2, TrendingUp, PieChart as PieIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import statisticsService from '../../services/statisticsService';
import { users as userService } from '../../services/apiService';
import { Loader } from '../UI/LoadingSpinner';
import ManagerFilterPanel from './ManagerFilterPanel';
import { PAGE_KEYS } from '../../constants/savedFilterPageKeys';
import { Pagination } from '@anyemp/ui-kit';
import { PAGINATION_OPTIONS } from '../../utils/paginationConstants';
import Avatar from '../UI/Avatar';
import ManagerPortraitDetailsModal from './ManagerPortraitDetailsModal';
import { CHART_COLORS } from '../../utils/chartTheme';
import { ChartCard, TrendLineChart, HorizontalBarChart, DonutChart } from '../../utils/chartComponents';
import MatrixTable from './MatrixTable';
import StatisticsTable from './StatisticsTable';

const ManagerPortraitStats = () => {
  const getCurrentMonthDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { startDate: fmt(firstDay), endDate: fmt(lastDay) };
  };

  const currentMonthDates = getCurrentMonthDates();

  const [stats, setStats] = useState([]);
  const [globalTotals, setGlobalTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_items: 0, items_per_page: 20 });
  const [filters, setFilters] = useState({
    startDate: currentMonthDates.startDate,
    endDate: currentMonthDates.endDate,
    manager_ids: [],
    limit: 20,
    offset: 0,
    period: ''
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  useEffect(() => { fetchStats(); }, [filters.limit, filters.offset]);
  useEffect(() => { fetchManagers(); }, []);

  useEffect(() => {
    if ((viewMode === 'matrix' || viewMode === 'charts') && !filters.period) {
      setFilters(prev => ({ ...prev, period: 'day' }));
    }
  }, [viewMode]);

  useEffect(() => {
    if ((viewMode === 'matrix' || viewMode === 'charts') && filters.period) {
      fetchStats();
    }
  }, [filters.period, viewMode]);

  const fetchManagers = async () => {
    try {
      setManagersLoading(true);
      const response = await userService.getAllUsers();
      if (response.success && response.data) {
        const usersList = response.data.users || response.data;
        setManagers(Array.isArray(usersList) ? usersList.map(u => ({ value: String(u.id), label: u.name || u.email || u.id })) : []);
      }
    } catch (err) {
      toast.error('Failed to load managers list');
    } finally {
      setManagersLoading(false);
    }
  };

  const fetchStats = async (overrideFilters = null) => {
    try {
      setLoading(true);
      setError(null);

      const f = overrideFilters ?? filters;

      const effectivePeriod = viewMode === 'matrix' ? (f.period || 'day') : f.period;
      const params = {
        ...f,
        manager_ids: f.manager_ids.length > 0 ? f.manager_ids : undefined,
        startDate: f.startDate || undefined,
        endDate: f.endDate || undefined,
        period: effectivePeriod || undefined
      };

      const requests = [statisticsService.getManagerPortraitAdded(params)];
      if (viewMode === 'matrix' || viewMode === 'charts') {
        requests.push(statisticsService.getManagerPortraitTotals({
          startDate: params.startDate, endDate: params.endDate,
          manager_ids: params.manager_ids, period: params.period
        }));
      }

      const [response, totalsResponse] = await Promise.all(requests);

      let statsData = [];
      let paginationData = null;

      if (response) {
        if (Array.isArray(response)) {
          statsData = response;
        } else if (response.success && response.data && Array.isArray(response.data)) {
          statsData = response.data;
          paginationData = response.pagination;
        } else if (response.data && Array.isArray(response.data)) {
          statsData = response.data;
          paginationData = response.pagination;
        }
        setStats(statsData);
        if (paginationData) {
          const { total, limit, offset } = paginationData;
          setPagination({ current_page: Math.floor(offset / limit) + 1, total_pages: Math.ceil(total / limit), total_items: total, items_per_page: limit });
        }
      }

      if (totalsResponse) setGlobalTotals(totalsResponse.data || totalsResponse);
    } catch (err) {
      setError(err.message || 'Failed to load statistics');
      toast.error('Failed to load portrait statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setFilters(prev => ({ ...prev, offset: 0 }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
    fetchStats();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      startDate: currentMonthDates.startDate,
      endDate: currentMonthDates.endDate,
      manager_ids: [],
      limit: 20,
      offset: 0,
      period: (viewMode === 'matrix' || viewMode === 'charts') ? 'day' : ''
    };
    setFilters(resetFilters);
    setPagination({ current_page: 1, total_pages: 1, total_items: 0, items_per_page: 20 });
    fetchStats(resetFilters);
  };

  const handlePageChange = (newPage) => {
    const newOffset = (newPage - 1) * filters.limit;
    setFilters(prev => ({ ...prev, offset: newOffset }));
    setPagination(prev => ({ ...prev, current_page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setFilters(prev => ({ ...prev, limit: newLimit, offset: 0 }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleViewModeChange = (mode) => {
    if (mode === 'matrix' || mode === 'charts') setLoading(true);
    setViewMode(mode);
  };

  const exportCSV = () => {
    if (!stats || stats.length === 0) { toast.error('No data to export'); return; }
    const headers = ['Manager', 'Email', 'Total Portraits'];
    const rows = [headers, ...stats.map(item => {
      const m = item.manager || {};
      return [m.name || 'Unknown', m.email || '', item.total_portraits || 0];
    })];
    const content = rows.map(r => r.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `portraits-by-managers-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const rowKeyManager = useCallback(
    (item) => item.manager?.id || item.manager?.email || JSON.stringify(item),
    []
  );

  const visibleColumns = useMemo(
    () => ({ manager: true, total_portraits: true, by_period: !!filters.period }),
    [filters.period]
  );

  const columns = useMemo(() => {
    const cols = [
      {
        key: 'manager', label: 'Manager', sortable: true,
        render: (item) => {
          const m = item.manager || {};
          return (
            <div className="flex min-w-0 items-center gap-3">
              <Avatar avatar={m.avatar} name={m.name || 'Unknown'} size="sm" />
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">{m.name || 'Unknown Manager'}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{m.email || ''}</div>
              </div>
            </div>
          );
        }
      },
      {
        key: 'total_portraits', label: 'Portraits', sortable: true,
        render: (item) => <span className="font-semibold text-gray-900 dark:text-gray-100">{item.total_portraits || 0}</span>
      }
    ];

    if (filters.period && stats.some(s => s.by_period && s.by_period.length > 0)) {
      cols.push({
        key: 'by_period', label: 'By Periods', sortable: false,
        render: (item) => {
          if (!item.by_period || item.by_period.length === 0) return <span className="text-gray-400">No data</span>;
          return (
            <div className="flex flex-col gap-1">
              {item.by_period.slice(0, 3).map((p, i) => (
                <div key={i} className="text-xs text-gray-600 dark:text-gray-400">
                  {new Date(p.period).toLocaleDateString()}: {p.count}
                </div>
              ))}
              {item.by_period.length > 3 && <span className="text-xs text-gray-400">+{item.by_period.length - 3} more</span>}
            </div>
          );
        }
      });
    }
    return cols;
  }, [filters.period, stats]);

  const generateDateColumns = useMemo(() => {
    if (!filters.startDate || !filters.endDate) return [];
    const period = filters.period || 'day';
    const columns = [];
    const start = new Date(filters.startDate);
    const end = new Date(filters.endDate);

    if (period === 'day') {
      let current = new Date(start);
      while (current <= end) {
        const dateStr = current.toISOString().split('T')[0];
        const dayOfWeek = current.getDay();
        columns.push({ key: dateStr, dateStr, label: `${current.getDate()}`, sublabel: (dayOfWeek === 0 || dayOfWeek === 6) ? 'wknd' : current.toLocaleDateString('en-US', { month: 'short' }), isWeekend: dayOfWeek === 0 || dayOfWeek === 6 });
        current.setDate(current.getDate() + 1);
      }
    } else if (period === 'week') {
      let current = new Date(start);
      let w = 1;
      while (current <= end) {
        columns.push({ key: current.toISOString().split('T')[0], dateStr: current.toISOString().split('T')[0], label: `${w}`, sublabel: 'wk', isWeekend: false });
        current.setDate(current.getDate() + 7);
        w++;
      }
    } else if (period === 'month') {
      let current = new Date(start.getFullYear(), start.getMonth(), 1);
      const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
      while (current <= endMonth) {
        const dateStr = current.toISOString().split('T')[0];
        columns.push({ key: dateStr, dateStr, label: current.toLocaleDateString('en-US', { month: 'short' }), sublabel: String(current.getFullYear()), isWeekend: false });
        current.setMonth(current.getMonth() + 1);
      }
    }
    return columns;
  }, [filters.startDate, filters.endDate, filters.period]);

  const matrixData = useMemo(() => {
    if (!stats.length || !generateDateColumns.length) return [];
    return stats.map(manager => {
      const periodMap = {};
      (manager.by_period || []).forEach(p => { periodMap[typeof p.period === 'string' ? p.period.split('T')[0] : p.period] = parseInt(p.count) || 0; });
      const days = {};
      generateDateColumns.forEach(col => { days[col.dateStr] = periodMap[col.dateStr] || 0; });
      return { ...manager, days };
    });
  }, [stats, generateDateColumns]);

  const matrixColumnTotals = useMemo(() => {
    if (!globalTotals?.by_period || !generateDateColumns.length) return {};
    const periodMap = {};
    globalTotals.by_period.forEach(p => { periodMap[typeof p.period === 'string' ? p.period.split('T')[0] : p.period] = parseInt(p.count) || 0; });
    const totals = {};
    generateDateColumns.forEach(col => { totals[col.dateStr] = periodMap[col.dateStr] || 0; });
    return totals;
  }, [globalTotals, generateDateColumns]);

  const matrixGrandTotal = globalTotals?.grand_total ?? 0;

  const renderChartsView = () => {
    if (loading) return <div className="flex justify-center items-center min-h-[400px]"><Loader /></div>;
    if (!stats || stats.length === 0) return <div className="flex justify-center items-center min-h-[300px] text-gray-400 dark:text-gray-500">No data for selected filters</div>;

    const trendData = (globalTotals?.by_period || []).map(p => ({ date: typeof p.period === 'string' ? p.period.split('T')[0] : p.period, count: parseInt(p.count) || 0 })).sort((a, b) => a.date.localeCompare(b.date));
    const managerData = stats.map(item => ({ name: (item.manager || {}).name || `ID: ${item.manager_id?.slice(0, 8)}`, total: item.total_portraits || 0 })).sort((a, b) => b.total - a.total);
    const pieData = managerData.slice(0, 5).map((item, i) => ({ name: item.name, value: item.total, color: CHART_COLORS[i % CHART_COLORS.length] }));
    const fmtDate = (d) => { if (!d) return ''; const p = d.split('-'); return p.length === 3 ? `${p[2]}.${p[1]}` : d; };

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Portrait Upload Trend" icon={TrendingUp} hint={trendData.length === 0 ? 'Select a period grouping to see the trend' : null}>
          <TrendLineChart data={trendData} color="#a855f7" name="Portraits" tickFormatter={fmtDate} />
        </ChartCard>
        <ChartCard title="Portraits by Manager" icon={BarChart2} hint={managerData.length === 0 ? 'No data available' : null}>
          <HorizontalBarChart data={managerData} color="#a855f7" name="Total Portraits" />
        </ChartCard>
        <ChartCard title="Top Managers Distribution" icon={PieIcon} hint={pieData.length === 0 ? 'No data available' : null}>
          <DonutChart data={pieData} />
        </ChartCard>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <Image className="h-8 w-8 text-purple-500" />
          <div>
            <h2 className="break-words text-2xl font-bold text-gray-900 dark:text-gray-100">Portraits by Managers</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Track portrait uploads by managers</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex max-w-full overflow-x-auto rounded-lg border border-gray-300 dark:border-gray-600">
            {[['list', List, 'List'], ['matrix', Grid, 'Matrix'], ['charts', BarChart2, 'Charts']].map(([mode, Icon, label], idx) => (
              <button key={mode} onClick={() => handleViewModeChange(mode)} title={`${label} view`}
                className={`flex shrink-0 items-center gap-1.5 px-3 py-2 text-sm transition-colors ${idx > 0 ? 'border-l border-gray-300 dark:border-gray-600' : ''} ${viewMode === mode ? 'bg-primary-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                <Icon className="h-4 w-4" /><span>{label}</span>
              </button>
            ))}
          </div>

          <button onClick={exportCSV} disabled={!stats.length} title="Export to CSV"
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            <Download className="h-4 w-4" /><span>CSV</span>
          </button>

          <button onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors">
            <Filter className="h-4 w-4" /><span>Filters</span>
          </button>
        </div>
      </div>

      {filtersExpanded && (
        <ManagerFilterPanel
          filters={filters}
          managers={managers}
          managersLoading={managersLoading}
          viewMode={viewMode}
          onFilterChange={(updates) => setFilters(prev => ({ ...prev, ...updates }))}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
          pageKey={PAGE_KEYS.STATS_MANAGER_PORTRAIT}
        />
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {loading ? (
          <div className="flex justify-center items-center py-12"><Loader /></div>
        ) : error ? (
          <div className="text-center py-8 text-red-600 dark:text-red-400">{error}</div>
        ) : viewMode === 'list' ? (
          <StatisticsTable
            data={stats}
            columns={columns}
            onRowClick={setSelectedManager}
            isLoading={loading}
            rowKey={rowKeyManager}
            visibleColumns={visibleColumns}
            footer={
              <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.total_pages}
                totalItems={pagination.total_items}
                itemsPerPage={pagination.items_per_page}
                onPageChange={handlePageChange}
                showResults="range"
                perPageOptions={PAGINATION_OPTIONS}
                perPage={filters.limit}
                onPerPageChange={(v) => handleLimitChange(parseInt(v))}
                showOnSinglePage
                hasFirstLast
                hasGoToPage
              />
            }
          />
        ) : viewMode === 'matrix' ? (
          <MatrixTable data={matrixData} loading={loading} dateColumns={generateDateColumns}
            columnTotals={matrixColumnTotals} grandTotal={matrixGrandTotal} totalKey="total_portraits"
            startIndex={filters.offset} onManagerClick={setSelectedManager}
            pagination={pagination} onPageChange={handlePageChange} itemsPerPage={filters.limit} />
        ) : renderChartsView()}
      </div>

      {selectedManager && (
        <ManagerPortraitDetailsModal manager={selectedManager} filters={filters} onClose={() => setSelectedManager(null)} />
      )}
    </div>
  );
};

export default ManagerPortraitStats;
